// Analyse: paste a game, get back the positions worth replaying.

import { useEffect, useRef, useState } from "react";
import { errorText, isSignedOut } from "../app/api.ts";
import { Icon } from "../app/icons.tsx";
import { KIND } from "../app/kinds.ts";
import { STRICTNESS, prefs } from "../app/prefs.ts";
import { navigate } from "../app/router.ts";
import { reportCache, reportKey, solvedCache } from "../app/solved.ts";
import type { ScreenProps } from "../app/App.tsx";
import { GameView } from "./Game.tsx";
import { NeedsColor, ProgressCard, runAnalysis, type Job } from "./analysis.tsx";

type Status = { kind: "idle" } | { kind: "running"; job: Job } | { kind: "error"; text: string } | { kind: "which"; names: { white: string; black: string } };

export function AnalyseScreen({ query }: ScreenProps) {
  const [input, setInput] = useState(query.get("input") || "");
  const [color, setColor] = useState(query.get("color") || "");
  const [strictness, setStrictness] = useState(() => {
    const asked = query.get("strictness");
    return asked && asked in STRICTNESS ? asked : prefs.strictness;
  });
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [pasted, setPasted] = useState<any>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const running = status.kind === "running";

  const grow = () => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(320, el.scrollHeight + 2) + "px";
  };
  useEffect(grow, [input]);

  async function submit(side?: string) {
    const text = input.trim();
    if (!text || running) return;
    setPasted(null);
    setStatus({ kind: "running", job: { status: "queued" } });
    try {
      const report = await runAnalysis({ input: text, color: side || color || null, config: STRICTNESS[strictness] }, (job) =>
        setStatus({ kind: "running", job })
      );
      setStatus({ kind: "idle" });
      if (report.game_id) {
        reportCache.set(reportKey(report.game_id, report.color), report);
        solvedCache.delete(reportKey(report.game_id, report.color));
        navigate(`/games/${report.game_id}/${report.color}`);
        return;
      }
      // A pasted PGN has no Lichess id, so it is not in the library: it is
      // shown here, and nothing about it is remembered as solved.
      setPasted(report);
    } catch (err) {
      if (isSignedOut(err)) return;
      if (err instanceof NeedsColor) setStatus({ kind: "which", names: err.names });
      else setStatus({ kind: "error", text: errorText(err) });
    }
  }

  // A link that carries the game analyses it at once.
  useEffect(() => {
    if (query.get("input")) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (pasted) {
    return (
      <div className="screen game-screen">
        <GameView
          report={pasted}
          solved={new Set()}
          lead={
            <button type="button" className="btn menu-btn" onClick={() => setPasted(null)}>
              <Icon name="back" />
              Back
            </button>
          }
          head={<div className="notice">Pasted games are analysed but not added to your library, so what you solve here is not remembered.</div>}
        />
      </div>
    );
  }

  const seg = (options: { value: string; label: string; title?: string }[], value: string, onChange: (v: string) => void) => (
    <div className="seg" role="group">
      {options.map((opt) => (
        <button key={opt.value} type="button" aria-pressed={opt.value === value} title={opt.title} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="screen">
      <div className="screen-head">
        <div>
          <h1>Analyse a game</h1>
          <p>Paste a game and get back the positions worth replaying.</p>
        </div>
      </div>
      <div className="card pad">
        <form
          className="analyse-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div>
            <label className="label" htmlFor="analyse-input">
              Game
            </label>
            <textarea
              id="analyse-input"
              ref={area}
              className="field"
              rows={1}
              placeholder="https://lichess.org/jGSZweNo/black — or a game id, or a PGN"
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="off"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !input.includes("\n")) {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
            <p className="help">
              A Lichess link, a bare game id, or a pasted PGN. The <code>/black</code> on the end of a Lichess URL already says whose moves to judge.
            </p>
          </div>
          <div className="analyse-options">
            <div className="opt">
              <span className="label">Judge</span>
              {seg(
                [
                  { value: "", label: "From the link" },
                  { value: "white", label: "White" },
                  { value: "black", label: "Black" },
                ],
                color,
                setColor
              )}
            </div>
            <div className="opt">
              <span className="label">Strictness</span>
              {seg(
                [
                  { value: "strict", label: "Strict", title: "Only tactics whose answer is unmistakable" },
                  { value: "normal", label: "Normal" },
                  { value: "loose", label: "Loose", title: "Widens both nets" },
                ],
                strictness,
                (v) => setStrictness(v as typeof strictness)
              )}
            </div>
          </div>
          <div className="btn-row">
            <button type="submit" className="btn btn-primary btn-lg" disabled={running}>
              Find my mistakes
            </button>
          </div>
        </form>
      </div>
      <div>
        {status.kind === "running" ? <ProgressCard job={status.job} /> : null}
        {status.kind === "error" ? <div className="notice error">{status.text}</div> : null}
        {status.kind === "which" ? (
          <div className="notice accent">
            <strong>Which side were you playing?</strong>
            <div className="btn-row">
              {(["white", "black"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  className="btn"
                  onClick={() => {
                    setColor(side);
                    void submit(side);
                  }}
                >
                  {status.names[side]} ({side})
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div className="kinds" style={{ marginTop: "22px" }}>
        <div className="kind">
          <h3>
            <span className="badge tactic">{KIND.tactic.label}</span>
          </h3>
          <p>A forced win was there and you played something else. Find it — there is one answer.</p>
        </div>
        <div className="kind">
          <h3>
            <span className="badge only_move">{KIND.only_move.label}</span>
          </h3>
          <p>Nothing was winning, but exactly one move held. Find it — there is one answer.</p>
        </div>
        <div className="kind">
          <h3>
            <span className="badge blunder">{KIND.blunder.label}</span>
          </h3>
          <p>Several moves held and you picked one that lost. Play anything that holds.</p>
        </div>
      </div>
    </div>
  );
}
